import Highcharts from 'highcharts'

/**
 * Checks if two bounding boxes overlap. It checks for horizontal and vertical overlap.
 * Only when both overlap do the boxes overlap.
 */
const boxesOverlap = (boundingBoxLabelA, boundingBoxLabelB) => {
  const horizontalOverlap =
    boundingBoxLabelA.x < boundingBoxLabelB.x + boundingBoxLabelB.width && boundingBoxLabelA.x + boundingBoxLabelA.width > boundingBoxLabelB.x
  const verticalOverlap =
    boundingBoxLabelA.y < boundingBoxLabelB.y + boundingBoxLabelB.height && boundingBoxLabelA.y + boundingBoxLabelA.height > boundingBoxLabelB.y
  return horizontalOverlap && verticalOverlap
}

/**
 * Helper function to get absolute rectangle of a label item in plot area
 * To get the absolute position, we need to add the item's x/y and its bbox x/y
 */
const rectangleBoxOfLabel = (item, yOverride = undefined) => {
  const xAbs = (item.x ?? 0) + (item.bbox.x ?? 0)
  const yAbs = (yOverride ?? item.y) + (item.bbox.y ?? 0)
  return { x: xAbs, y: yAbs, width: item.bbox.width, height: item.bbox.height }
}

/**
 * Tries to place newLabel avoiding overlaps with placedLabels.
 * - newLabel: object with { label, bbox, x, y } (y = current translateY)
 * - placedLabels: array of same structure of already fixed labels
 * - chart: Highcharts chart (for plot area)
 * - opts: { padding, maxTries } padding default 4px, maxTries default 5
 *
 * The function applies the translateY with label.graphic.attr({ translateY })
 * and returns the final y position.
 */
const placeLabelAvoidingOverlaps = (newLabel, placedLabels, chart, opts = {}) => {
  const maxTries = opts.maxTries ?? 10
  const padding = opts.padding ?? 4
  const plotTop = chart.plotTop
  const plotBottom = chart.plotTop + chart.plotHeight

  // start position (pixels)
  let currentY = newLabel.y

  // if there are no placed labels, set position directly
  if (!placedLabels || placedLabels.length === 0) return currentY

  // function to check if at given y position it overlaps any placed label
  const overlapsWithAnyLabel = yToTest => {
    const newBox = rectangleBoxOfLabel(newLabel, yToTest)
    for (const placedLabel of placedLabels) {
      const existingBox = rectangleBoxOfLabel(placedLabel)
      if (boxesOverlap(newBox, existingBox)) return true
    }
    return false
  }

  // try moving down step by step until no overlap
  const step = newLabel.bbox.height + padding
  const originalY = newLabel.y
  let tries = 0

  // try moving down only
  while (overlapsWithAnyLabel(currentY) && tries++ < maxTries) {
    currentY += step
    // if we exceed plot bottom, break and try fallback
    if (currentY + newLabel.bbox.height > plotBottom) break
  }

  // if still overlapping or out of bounds, try moving up from original position
  // this is necessary because sometimes the label y position starts near the bottom.
  // Currently i don't know why exactly, but it has something to do with the method in
  // TimelineTools.js
  if (overlapsWithAnyLabel(currentY) || currentY + newLabel.bbox.height > plotBottom) {
    let upY = originalY - step
    tries = 0
    while (tries++ < maxTries) {
      // no more space upwards
      if (upY < plotTop) break

      if (!overlapsWithAnyLabel(upY)) {
        currentY = upY
        break
      }
      upY -= step
    }
  }

  // if still overlapping, clamp within plot area (best effort)
  if (currentY + newLabel.bbox.height > plotBottom) currentY = plotBottom - newLabel.bbox.height

  // apply new position to label
  newLabel.label.graphic.attr({ translateY: currentY })

  return currentY
}

const useLabelPositioningWithAvoidingOverlaps = chart => {
  // add event to Highcharts render to reposition labels
  Highcharts.addEvent(chart, 'render', function () {
    const placedLabels = [] // already fixed labels

    // get all annotation labels and process them in desired order.
    // here: sort by y position (top -> bottom) so that "higher" labels are fixed first.
    const allLabels = []
    chart.annotations?.forEach(annotation => {
      annotation.labels?.forEach(label => {
        const elem = label.graphic?.element
        if (!elem) return

        const bbox = elem.getBBox()
        const x = label.graphic.translateX || 0
        const y = label.graphic.translateY || 0
        allLabels.push({ label, bbox, x, y })
      })
    })

    // sort from top to bottom (smaller y position first)
    allLabels.sort((a, b) => a.y - b.y)

    for (const item of allLabels) {
      // place item in relation to already placed labels
      const finalYPosition = placeLabelAvoidingOverlaps(item, placedLabels, chart)

      // push in placed with final position (important for further checks)
      placedLabels.push({
        label: item.label,
        bbox: item.bbox,
        x: item.x,
        y: finalYPosition,
      })
    }
  })
}

export { useLabelPositioningWithAvoidingOverlaps }
